// crewbit-admin/src/components/money/settings/MileageRatesEditor.tsx
//
// Paired editor for mileage rates: rate-1 (km 1 -> threshold), rate-2
// (km > threshold), threshold (km). Inline-save with 1.5s debounce per
// Decision §Settings.D3 — single update RPC call per save with all 3 fields.
//
// Validation:
//   rate-1, rate-2: 0 ≤ rate ≤ 1.00 (numeric)
//   threshold: > 0 integer (km)
// Server (`update_business_money_settings`) re-validates and returns
// `INVALID_MILEAGE_RATE` on bad rate; client gates save to avoid round-trips.
//
// Mirrors crewbit-mobile/components/money/settings/MileageRatesEditor.tsx.

import { useEffect, useRef, useState } from 'react';
import { palette, radii, spacing, typography } from '../../../styles/tokens';
import SaveConfirmationPill from './SaveConfirmationPill';

const DEBOUNCE_MS = 1500;
const PILL_VISIBLE_MS = 2000;

export type MileageSaveResult =
  | { success: true }
  | { success: false; message?: string };

interface MileageRatesEditorProps {
  label: string;
  description?: string;
  rate1Label: string;
  rate2Label: string;
  thresholdLabel: string;
  craReferenceCopy?: string;
  invalidRateLabel: string;
  invalidThresholdLabel: string;
  rate1: number | null;
  rate2: number | null;
  threshold: number | null;
  onSave: (next: {
    mileageRate1: number | null;
    mileageRate2: number | null;
    mileageThreshold: number | null;
  }) => Promise<MileageSaveResult>;
}

function fmt(n: number | null): string {
  if (n === null || n === undefined) return '';
  return String(n);
}

function parseNum(s: string): number | null {
  if (s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function rateValid(s: string): boolean {
  if (s === '') return true;
  const n = Number(s);
  return Number.isFinite(n) && n >= 0 && n <= 1;
}

function thresholdValid(s: string): boolean {
  if (s === '') return true;
  const n = Number(s);
  return Number.isFinite(n) && n > 0;
}

export default function MileageRatesEditor({
  label,
  description,
  rate1Label,
  rate2Label,
  thresholdLabel,
  craReferenceCopy,
  invalidRateLabel,
  invalidThresholdLabel,
  rate1,
  rate2,
  threshold,
  onSave,
}: MileageRatesEditorProps) {
  const [r1, setR1] = useState<string>(fmt(rate1));
  const [r2, setR2] = useState<string>(fmt(rate2));
  const [th, setTh] = useState<string>(fmt(threshold));
  const [pillVisible, setPillVisible] = useState(false);
  const [pillKind, setPillKind] = useState<'success' | 'warning'>('success');
  const [pillLabel, setPillLabel] = useState<string | undefined>(undefined);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pillTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef<boolean>(true);

  // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional sync of upstream rate1 into local draft after refetch
  useEffect(() => { setR1((prev) => (prev === fmt(rate1) ? prev : fmt(rate1))); }, [rate1]);
  // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional sync of upstream rate2 into local draft after refetch
  useEffect(() => { setR2((prev) => (prev === fmt(rate2) ? prev : fmt(rate2))); }, [rate2]);
  // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional sync of upstream threshold into local draft after refetch
  useEffect(() => { setTh((prev) => (prev === fmt(threshold) ? prev : fmt(threshold))); }, [threshold]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (pillTimeoutRef.current) clearTimeout(pillTimeoutRef.current);
    };
  }, []);

  const flashPill = (kind: 'success' | 'warning', flashLabel?: string) => {
    if (pillTimeoutRef.current) clearTimeout(pillTimeoutRef.current);
    setPillKind(kind);
    setPillLabel(flashLabel);
    setPillVisible(true);
    pillTimeoutRef.current = setTimeout(() => {
      if (mountedRef.current) setPillVisible(false);
    }, PILL_VISIBLE_MS);
  };

  const scheduleSave = (nextR1: string, nextR2: string, nextTh: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!rateValid(nextR1) || !rateValid(nextR2) || !thresholdValid(nextTh)) return;
    debounceRef.current = setTimeout(async () => {
      const result = await onSave({
        mileageRate1: parseNum(nextR1),
        mileageRate2: parseNum(nextR2),
        mileageThreshold: parseNum(nextTh),
      });
      if (!mountedRef.current) return;
      if (result.success) flashPill('success');
      else flashPill('warning', result.message);
    }, DEBOUNCE_MS);
  };

  const onR1 = (text: string) => { setR1(text); scheduleSave(text, r2, th); };
  const onR2 = (text: string) => { setR2(text); scheduleSave(r1, text, th); };
  const onTh = (text: string) => { setTh(text); scheduleSave(r1, r2, text); };

  const r1Error = !rateValid(r1);
  const r2Error = !rateValid(r2);
  const thError = !thresholdValid(th);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs, padding: spacing.md }}>
      <div style={{ fontSize: typography.size.subheading, fontWeight: 600, color: palette.g800 }}>
        {label}
      </div>
      {description && (
        <div style={{ fontSize: typography.size.caption, color: palette.g500, lineHeight: 1.4 }}>
          {description}
        </div>
      )}
      {craReferenceCopy && (
        <div style={{ fontSize: typography.size.caption, color: palette.g500, fontStyle: 'italic' }}>
          {craReferenceCopy}
        </div>
      )}

      <div style={{ display: 'flex', gap: spacing.sm, marginTop: spacing.xs }}>
        <Field label={rate1Label} error={r1Error ? invalidRateLabel : null}>
          <NumInput value={r1} onChange={onR1} placeholder="0.70" hasError={r1Error} />
        </Field>
        <Field label={rate2Label} error={r2Error ? invalidRateLabel : null}>
          <NumInput value={r2} onChange={onR2} placeholder="0.64" hasError={r2Error} />
        </Field>
      </div>

      <div style={{ marginTop: spacing.xs }}>
        <Field label={thresholdLabel} error={thError ? invalidThresholdLabel : null}>
          <NumInput value={th} onChange={onTh} placeholder="5000" hasError={thError} />
        </Field>
      </div>

      <div style={{ minHeight: 18, marginTop: spacing.xxs }}>
        <SaveConfirmationPill visible={pillVisible} kind={pillKind} label={pillLabel} />
      </div>
    </div>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error: string | null;
  children: React.ReactNode;
}) {
  return (
    <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: spacing.xxs }}>
      <span style={{ fontSize: typography.size.caption, fontWeight: 600, color: palette.g600 }}>
        {label}
      </span>
      {children}
      {error && (
        <span style={{ fontSize: typography.size.caption, color: palette.amber, fontWeight: 600 }}>
          {error}
        </span>
      )}
    </label>
  );
}

function NumInput({
  value,
  onChange,
  placeholder,
  hasError,
}: {
  value: string;
  onChange: (s: string) => void;
  placeholder: string;
  hasError: boolean;
}) {
  return (
    <input
      type="text"
      inputMode="decimal"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        fontSize: typography.size.body,
        padding: spacing.sm,
        borderRadius: radii.sm,
        border: `1px solid ${hasError ? palette.amber : palette.g200}`,
        background: palette.white,
        color: palette.g800,
        fontFamily: 'inherit',
        fontVariantNumeric: 'tabular-nums',
        outline: 'none',
        width: '100%',
        boxSizing: 'border-box',
      }}
    />
  );
}
